// W2Cook Billing Dashboard — Settings Modal
// Covers: notification config, budget limits, logo upload

import { useState } from "react";
import type { NotificationConfig, BudgetLimits } from "../data/environments";
import type { Service } from "../data/services";
import { tokens as T } from "../theme/tokens";
import { Btn, Field, Modal, SectionLabel } from "./ui";

interface Props {
  notifications: NotificationConfig;
  limits:        BudgetLimits;
  services:      Service[];
  onSaveNotifications: (cfg: NotificationConfig) => void;
  onSaveLimits:        (limits: BudgetLimits) => void;
  onClose: () => void;
}

type Tab = "notifications" | "limits" | "logo";

export function SettingsModal({
  notifications, limits, services,
  onSaveNotifications, onSaveLimits, onClose,
}: Props) {
  const [tab,      setTab     ] = useState<Tab>("notifications");
  const [notifCfg, setNotifCfg] = useState<NotificationConfig>({ ...notifications });
  const [lim,      setLim     ] = useState<BudgetLimits>({
    totalMonthly: limits.totalMonthly,
    perCategory:  { ...limits.perCategory },
    perService:   { ...limits.perService },
  });
  const [testStatus, setTestStatus] = useState<{ ok: boolean; msg: string } | null>(null);
  const [testing, setTesting] = useState(false);

  const categories = Array.from(new Set(services.map(s => s.category))).sort();
  const activeServices = services.filter(s => s.status === "active");

  const setN = (k: keyof NotificationConfig, v: any) =>
    setNotifCfg((p: NotificationConfig) => ({ ...p, [k]: v }));
  const setAlert = (k: keyof NotificationConfig["alerts"], v: boolean) =>
    setNotifCfg((p: NotificationConfig) => ({ ...p, alerts: { ...p.alerts, [k]: v } }));

  const testEmail = async () => {
    setTesting(true);
    setTestStatus(null);
    try {
      const res = await fetch(notifCfg.smtpEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to:      notifCfg.recipientEmail,
          from:    notifCfg.senderEmail,
          subject: "✅ W2Cook Billing — Test notification",
          html:    "<h2>Test email from W2Cook Billing Dashboard</h2><p>Notifications are working correctly.</p>",
        }),
      });
      setTestStatus({ ok: res.ok, msg: res.ok ? "Test email sent successfully" : `Failed: HTTP ${res.status}` });
    } catch (e: any) {
      setTestStatus({ ok: false, msg: `Error: ${e?.message}` });
    }
    setTesting(false);
  };


  const TABS: { id: Tab; label: string }[] = [
    { id: "notifications", label: "📧 Notifications" },
    { id: "limits",        label: "💰 Budget Limits" },
    { id: "logo",          label: "🎨 Logo" },
  ];

  return (
    <Modal title="Settings" onClose={onClose} width={520}>
      {/* Tab bar */}
      <div style={{ display: "flex", gap: 6, marginBottom: 22, borderBottom: `1px solid ${T.border}`, paddingBottom: 14 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: "6px 14px", borderRadius: 8, fontSize: 13, cursor: "pointer", fontFamily: "inherit",
            background: tab === t.id ? `${T.ember}20` : "none",
            border: `1px solid ${tab === t.id ? T.ember : T.border}`,
            color: tab === t.id ? T.ember : T.textMuted,
          }}>{t.label}</button>
        ))}
      </div>

      {/* ── Notifications tab ── */}
      {tab === "notifications" && (
        <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <SectionLabel>EMAIL ALERTS</SectionLabel>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <div onClick={() => setN("enabled", !notifCfg.enabled)} style={{
                width: 36, height: 20, borderRadius: 10, background: notifCfg.enabled ? T.sage : T.border,
                position: "relative", transition: "background 0.2s", cursor: "pointer",
              }}>
                <div style={{
                  position: "absolute", top: 3, left: notifCfg.enabled ? 18 : 3,
                  width: 14, height: 14, borderRadius: "50%", background: "#fff",
                  transition: "left 0.2s",
                }} />
              </div>
              <span style={{ fontSize: 13, color: notifCfg.enabled ? T.sage : T.textMuted }}>
                {notifCfg.enabled ? "Enabled" : "Disabled"}
              </span>
            </label>
          </div>

          <Field label="RECIPIENT EMAIL" value={notifCfg.recipientEmail} onChange={v => setN("recipientEmail", v)} placeholder="bala@dhivinslabs.com" />
          <Field label="SENDER EMAIL" value={notifCfg.senderEmail} onChange={v => setN("senderEmail", v)} placeholder="billing@w2cook.com" />
          <Field label="SMTP CLOUD RUN ENDPOINT" value={notifCfg.smtpEndpoint} onChange={v => setN("smtpEndpoint", v)} placeholder="https://dlb-w2cook-mailer-xxx.run.app/send" secret={false} />

          <SectionLabel>ALERT TRIGGERS</SectionLabel>
          {([
            ["spendExceedsLimit",  "Monthly spend exceeds budget limit"],
            ["renewalWarning30d",  "Service renewal in 30 days"],
            ["renewalWarning7d",   "Service renewal in 7 days"],
          ] as const).map(([key, label]) => (
            <label key={key} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, cursor: "pointer" }}>
              <div onClick={() => setAlert(key, !notifCfg.alerts[key])} style={{
                width: 18, height: 18, borderRadius: 4, border: `2px solid ${notifCfg.alerts[key] ? T.ember : T.border}`,
                background: notifCfg.alerts[key] ? T.ember : "none", display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, cursor: "pointer",
              }}>
                {notifCfg.alerts[key] && <span style={{ color: "#fff", fontSize: 11, lineHeight: 1 }}>✓</span>}
              </div>
              <span style={{ fontSize: 13, color: T.textMuted }}>{label}</span>
            </label>
          ))}

          {testStatus && (
            <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 14, background: testStatus.ok ? `${T.sage}15` : `${T.ember}15`, border: `1px solid ${testStatus.ok ? T.sage : T.ember}40`, color: testStatus.ok ? T.sage : T.ember, fontSize: 13 }}>
              {testStatus.ok ? "✅" : "❌"} {testStatus.msg}
            </div>
          )}

          <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
            <Btn variant="ghost" onClick={testEmail} disabled={testing || !notifCfg.smtpEndpoint} style={{ flex: 1 }}>
              {testing ? "Sending…" : "Send Test Email"}
            </Btn>
            <Btn variant="primary" onClick={() => onSaveNotifications(notifCfg)} style={{ flex: 1 }}>Save</Btn>
          </div>
        </>
      )}

      {/* ── Limits tab ── */}
      {tab === "limits" && (
        <>
          <SectionLabel>TOTAL MONTHLY CEILING</SectionLabel>
          <Field
            label="MAX MONTHLY SPEND (USD) — 0 = no limit"
            type="number"
            value={lim.totalMonthly}
            onChange={v => setLim((p: BudgetLimits) => ({ ...p, totalMonthly: parseFloat(v) || 0 }))}
            placeholder="0"
          />

          <SectionLabel>PER-CATEGORY LIMITS</SectionLabel>
          {categories.map(cat => (
            <div key={cat} style={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: "0 12px", alignItems: "end" }}>
              <Field
                label={cat.toUpperCase()}
                type="number"
                value={lim.perCategory[cat] ?? 0}
                onChange={v => setLim((p: BudgetLimits) => ({ ...p, perCategory: { ...(p as BudgetLimits).perCategory, [cat]: parseFloat(v) || 0 } }))}
                placeholder="0"
              />
            </div>
          ))}

          <SectionLabel>PER-SERVICE LIMITS</SectionLabel>
          {activeServices.map(svc => (
            <div key={svc.id} style={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: "0 12px", alignItems: "end" }}>
              <Field
                label={`${svc.icon} ${svc.name.toUpperCase()}`}
                type="number"
                value={lim.perService[svc.id] ?? svc.budgetLimit}
                onChange={v => setLim((p: BudgetLimits) => ({ ...p, perService: { ...(p as BudgetLimits).perService, [svc.id]: parseFloat(v) || 0 } }))}
                placeholder="0"
              />
            </div>
          ))}

          <Btn variant="primary" onClick={() => onSaveLimits(lim)} style={{ width: "100%", marginTop: 8 }}>Save Limits</Btn>
        </>
      )}

      {/* ── Logo tab ── */}
      {tab === "logo" && (
        <>
          <SectionLabel>ORGANISATION LOGO</SectionLabel>
          <div style={{ padding: 20, background: T.surfaceAlt, borderRadius: 8, border: `1px solid ${T.border}`, textAlign: "center", marginBottom: 16 }}>
            <div style={{ background: "#0D2B1F", borderRadius: 10, padding: "14px 24px", display: "inline-block" }}>
              <img src="/dhivins-labs-logo.png" alt="Dhivins Labs" style={{ height: 52, objectFit: "contain" }} />
            </div>
          </div>
          <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6 }}>
            The logo is served from <code style={{ color: T.gold, fontSize: 12 }}>/public/dhivins-labs-logo.png</code> in the repo.
            To update it, replace that file and redeploy.
          </div>
        </>
      )}
    </Modal>
  );
}
